import { useMemo } from "react";
import { useParams } from "next/navigation";

import { DispensaryTags } from "@/custom-types/Dispensary";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getUserPrefersMedical } from "@/redux/selectors/user";
import isDispensaryDualLicense from "@/utils/dispensary/isDispensaryDualLicense";
import { dispensaryHref } from "@/utils/dispensaryUrls";

import AlertBanner from "@/components/botanic/AlertBanner";
import MedicalCrossIcon from "@/components/Icons/medical_cross.svg";

import links from "./links.json";

const MedicalInfo: React.FC<{
  dispensaryId: number;
  dispensaryRetailType: string;
  dispensaryTags: DispensaryTags[];
}> = ({ dispensaryId, dispensaryRetailType, dispensaryTags }) => {
  const params = useParams<{ slug: string }>();
  const countryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();

  const userPrefersMedical = useSelector(getUserPrefersMedical);

  const isDualLicense = isDispensaryDualLicense(dispensaryTags);

  const linkedDispensarySlug = (links as { [key: string]: string })[
    params?.slug || ""
  ];
  const linkedDispensaryHref = dispensaryHref({
    countryCode,
    retailType: dispensaryRetailType,
    slug: params?.slug,
  });

  // Memoized values to determine which version to show
  const stringifiedTags = JSON.stringify(dispensaryTags);
  // Show for dual licenses if Med is selected
  const showDualMenuMedical = useMemo(
    () => isDualLicense && userPrefersMedical,
    [userPrefersMedical, stringifiedTags],
  );
  // Show for medical dispensaries that are not recreational
  const showMedical = useMemo(
    () =>
      !isDualLicense &&
      dispensaryTags.includes("medical") &&
      !dispensaryTags.includes("recreational"),
    [stringifiedTags],
  );
  // Show for dispensaries that aren't dual license or medical if linked to another dispensary
  const showRecreational = useMemo(
    () =>
      !isDualLicense &&
      !dispensaryTags.includes("medical") &&
      !!linkedDispensarySlug,
    [stringifiedTags],
  );

  if (!(showDualMenuMedical || showMedical || showRecreational)) {
    return null;
  }

  const linkOnClick = () => {
    publishEvent({
      action: "Click",
      category: "Dispensary Menu",
      label: `Toggle to ${showMedical ? "Rec" : "Medical"} Menu`,
    });
    publishEvent({
      action: "click",
      category: "Med ID Required Banner",
      dispensaryId,
      label: "dispensary menu",
    });
  };

  const isLinkedDispensary = linkedDispensarySlug && !isDualLicense;

  const linkedMenuLinkText = `Shop the ${
    showMedical ? "recreational" : "medical"
  } menu instead`;

  const linkedMenuMessage =
    linkedDispensarySlug && showMedical
      ? "You need a medical ID to order from this menu."
      : "This is the recreational menu.";

  return (
    <AlertBanner
      className="mb-xl"
      heading={
        showDualMenuMedical || showMedical
          ? "MEDICAL ID REQUIRED"
          : "LOOKING FOR THE MEDICAL MENU?"
      }
      icon={<MedicalCrossIcon height="24" width="24" className="text-yellow" />}
      linkOnClick={linkOnClick}
      linkText={
        isLinkedDispensary ? linkedMenuLinkText : "Search doctors near you"
      }
      linkHref={
        isLinkedDispensary ? linkedDispensaryHref : "/medical-marijuana-doctors"
      }
      message={
        isLinkedDispensary
          ? linkedMenuMessage
          : "Don't have one yet? Get your med card quickly."
      }
      showCaret={showDualMenuMedical}
      trackImpression={() =>
        publishEvent({
          action: "impression",
          category: "Med ID Required Banner",
          dispensaryId,
          label: "dispensary menu",
        })
      }
    />
  );
};

export default MedicalInfo;
